import { ExpandMore } from '@mui/icons-material';
import { Accordion, AccordionDetails, AccordionSummary, Box } from '@mui/material';
import { useMemo, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Tooltip } from '~/components/Tooltip';
import { useAccordionIds } from '~/hooks/unique-id';
import { useSideNav } from '../context';
import type { NavItemConfig } from '../navItems';
import { NavItemLink } from './NavItems';

export const isNavItemSelected = (pathname: string, basePath: string) => {
  const normalizedBasePath = basePath.replace(/^\/+/, ''); // Remove leading slash if present
  return new RegExp(`^/networks/\\d+/${normalizedBasePath}`).test(pathname);
};

export interface NavGroupProps {
  initialOpen?: boolean;
  items: NavItemConfig[];
  onItemClick?: () => void;
  title: string;
}

export const NavGroup = ({ items, initialOpen = false, onItemClick, title }: NavGroupProps) => {
  const { navOpen: sideNavOpen } = useSideNav();
  const accordionIds = useAccordionIds();

  const location = useLocation();

  const navItems = useMemo(
    () =>
      items.map(({ icon, selected, text, to }) => ({
        icon,
        selected: isNavItemSelected(location.pathname, selected ?? to),
        text,
        to,
      })),
    [items, location.pathname],
  );

  const [open, setOpen] = useState(
    () => initialOpen || navItems.some((item) => item.selected) || navItems.length === 1,
  );

  if (navItems.length === 0) return null;

  return (
    <Accordion
      disableGutters
      onChange={() => setOpen((prev) => !prev)}
      expanded={open}
      sx={{
        backgroundColor: 'transparent',
        '&::before': {
          display: 'none',
        },
      }}
    >
      <AccordionSummary
        {...accordionIds}
        // Collapsed, the title is clipped to nothing and only the chevron's tooltip shows, so the
        // header names itself.
        aria-label={sideNavOpen ? undefined : title}
        expandIcon={
          sideNavOpen ? (
            <ExpandMore />
          ) : (
            <Tooltip title={title} placement="right">
              <ExpandMore />
            </Tooltip>
          )
        }
        sx={{
          backgroundColor: 'transparent',
          color: 'common.white',
          '& svg': {
            color: 'common.white',
          },
          '.MuiAccordionSummary-content': {
            fontSize: '.8rem',
            fontSpacing: '1px',
            fontWeight: 600,
            textTransform: 'uppercase',
          },
        }}
      >
        {title}
      </AccordionSummary>
      <AccordionDetails
        sx={{
          padding: (theme) => theme.spacing(0),
        }}
      >
        <Box
          sx={{
            width: '100%',
          }}
        >
          {navItems.map((item) => (
            <NavItemLink key={item.to} {...item} onClick={onItemClick} />
          ))}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
};
